/**
 * Global modal/drawer focus manager: ref-count and body class.
 * When any modal or full-screen drawer is open, we add body.talentfunn-modal-open
 * so global CSS can hide header, bottom nav, and footer.
 * Use this hook in every full-screen overlay (ConfirmDialog, modals, drawers).
 * Cleanup on unmount ensures count returns to 0 even on crash/unmount.
 */
import { useEffect, useRef } from 'react';
import { devLog } from '../logger';

const BODY_CLASS = 'talentfunn-modal-open';
const BODY_CLASS_FULLSCREEN = 'talentfunn-fullscreen-modal-open';

let modalCount = 0;
let fullscreenCount = 0;

function syncBodyClass() {
  if (typeof document === 'undefined') return;
  const hasOpen = modalCount > 0;
  if (hasOpen) {
    document.body.classList.add(BODY_CLASS);
    devLog(`[useModalFocus] modal count=${modalCount} → body.${BODY_CLASS} added`);
  } else {
    document.body.classList.remove(BODY_CLASS);
    devLog(`[useModalFocus] modal count=0 → body.${BODY_CLASS} removed`);
  }

  const hasFullscreen = fullscreenCount > 0;
  if (hasFullscreen) {
    document.body.classList.add(BODY_CLASS_FULLSCREEN);
    devLog(
      `[useModalFocus] fullscreen count=${fullscreenCount} → body.${BODY_CLASS_FULLSCREEN} added`
    );
  } else {
    document.body.classList.remove(BODY_CLASS_FULLSCREEN);
    devLog(`[useModalFocus] fullscreen count=0 → body.${BODY_CLASS_FULLSCREEN} removed`);
  }

  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('talentfunn:modal-focus-changed', {
        detail: { isOpen: hasOpen, count: modalCount, hasFullscreen },
      })
    );
  }
}

/**
 * Call when a full-screen modal or drawer is mounted.
 * On unmount (or when isActive becomes false), the count is decremented.
 * Pass isActive: true when the overlay is visible (e.g. isOpen for a dialog).
 */
export function useModalFocus(isActive: boolean, options?: { fullscreen?: boolean }) {
  const didIncrement = useRef(false);
  const didIncrementFullscreen = useRef(false);

  useEffect(() => {
    if (!isActive) return;

    modalCount += 1;
    didIncrement.current = true;
    if (options?.fullscreen) {
      fullscreenCount += 1;
      didIncrementFullscreen.current = true;
    }
    syncBodyClass();

    return () => {
      if (didIncrement.current) {
        modalCount = Math.max(0, modalCount - 1);
        didIncrement.current = false;
        if (didIncrementFullscreen.current) {
          fullscreenCount = Math.max(0, fullscreenCount - 1);
          didIncrementFullscreen.current = false;
        }
        syncBodyClass();
      }
    };
  }, [isActive, options?.fullscreen]);
}
